import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { ThemeProvider } from "@material-ui/core/styles";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

import MonitoramentoPrecoFormSections from "./components/MonitoramentoPrecoFormSections";
import MonitoramentoPrecoHeader from "./components/MonitoramentoPrecoHeader";
import MonitoramentoPrecoLoading from "./components/MonitoramentoPrecoLoading";
import MonitoramentoPrecoCadastroConcorrenteDialog from "./components/MonitoramentoPrecoCadastroConcorrenteDialog";
import MonitoramentoPrecoMinhasColetasDialog from "./components/MonitoramentoPrecoMinhasColetasDialog";
import MonitoramentoPrecoUnavailable from "./components/MonitoramentoPrecoUnavailable";
import { STORAGE_KEY } from "./components/mpConstants";
import {
  buildConcorrentesRowsFromProduto,
  buildRazaoSocialOption,
  compareConcorrenteAlfabetico,
  mapConcorrenteRowToApi,
  mapLojaFromApi,
  mapLojaToApiPayload,
  mapMarcasConcorrentesFromApi,
  mapProdutosReferenciaCatalogoFromApi,
  mapProdutoToApiPayload,
  normalizeLojaForm,
  produtoPermiteCadastroConcorrente,
  unwrapApiResult,
} from "./components/mpMappers";
import { mpTheme, useMonitoramentoStyles } from "./components/mpStyles";
import {
  fileToBase64,
  getRepresentativeDisplayName,
  resolvePrecoKarstenNumerico,
} from "./components/mpUtils";

const OPCOES_CADASTRO_INICIAL = {
  cama: { nomesComerciais: [], gramaturas: [], tecidoBases: [] },
  banho: { nomesComerciais: [], gramaturas: [], tecidoBases: [] },
};

function useCatalogoInicial() {
  const [lojasCatalogo, setLojasCatalogo] = useState([]);
  const [produtosCatalogo, setProdutosCatalogo] = useState([]);
  const [marcasConcorrentes, setMarcasConcorrentes] = useState([]);
  const [opcoesCadastroPorCategoria, setOpcoesCadastroPorCategoria] = useState(
    OPCOES_CADASTRO_INICIAL,
  );
  const [pageState, setPageState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPageState("loading");
      try {
        const [lojasRes, prodRes, marcasRes] = await Promise.all([
          Api.get("lojas", "priceMonitoring", "", true),
          Api.get("produtos-referencia", "priceMonitoring", "", true),
          Api.get("concorrentes", "priceMonitoring", "", true),
        ]);
        if (cancelled) return;
        const lojasRaw = unwrapApiResult(lojasRes);
        const prodRaw = unwrapApiResult(prodRes);
        const marcasRaw = unwrapApiResult(marcasRes);
        const lojas = Array.isArray(lojasRaw)
          ? lojasRaw.map(mapLojaFromApi)
          : [];
        const catalogo = mapProdutosReferenciaCatalogoFromApi(prodRaw);
        setLojasCatalogo(lojas);
        setProdutosCatalogo(catalogo.produtos);
        setMarcasConcorrentes(mapMarcasConcorrentesFromApi(marcasRaw));
        setOpcoesCadastroPorCategoria(catalogo.opcoesCadastro);
        if (lojas.length === 0 || catalogo.produtos.length === 0) {
          setPageState("unavailable");
        } else {
          setPageState("ready");
        }
      } catch {
        if (!cancelled) {
          setLojasCatalogo([]);
          setProdutosCatalogo([]);
          setMarcasConcorrentes([]);
          setOpcoesCadastroPorCategoria(OPCOES_CADASTRO_INICIAL);
          setPageState("unavailable");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    lojasCatalogo,
    produtosCatalogo,
    setProdutosCatalogo,
    marcasConcorrentes,
    opcoesCadastroPorCategoria,
    pageState,
  };
}

export default function MonitoramentoPreco() {
  const classes = useMonitoramentoStyles();
  const snackbar = useSnackbar();
  const snackbarRef = useRef(snackbar);
  snackbarRef.current = snackbar;

  const {
    lojasCatalogo,
    produtosCatalogo,
    setProdutosCatalogo,
    marcasConcorrentes,
    opcoesCadastroPorCategoria,
    pageState,
  } = useCatalogoInicial();

  const [tipoColeta, setTipoColeta] = useState("padrao");
  const [dataColeta, setDataColeta] = useState(() =>
    moment().format("DD-MM-YYYY"),
  );
  const [representante] = useState(() => getRepresentativeDisplayName());

  const [razaoSocial, setRazaoSocial] = useState(null);
  const [lojaCompleta, setLojaCompleta] = useState(null);

  const [produto, setProduto] = useState(null);
  const [precoKarstenLoja, setPrecoKarstenLoja] = useState(undefined);
  const [concorrentesRows, setConcorrentesRows] = useState([]);

  const [coletas, setColetas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [minhasColetasOpen, setMinhasColetasOpen] = useState(false);
  const [minhasColetasMountKey, setMinhasColetasMountKey] = useState(0);
  const [cadastroConcorrenteOpen, setCadastroConcorrenteOpen] = useState(false);
  const [concorrenteDestaqueId, setConcorrenteDestaqueId] = useState(null);
  const storageWarnedRef = useRef(false);
  const produtoSelecionadoIdRef = useRef(null);

  const razoesOptions = useMemo(() => {
    const map = new Map();
    lojasCatalogo.forEach((l) => {
      const key = l.razaoSocial;
      if (!map.has(key)) {
        map.set(key, buildRazaoSocialOption(key, l.cnpj));
      }
    });
    return Array.from(map.values());
  }, [lojasCatalogo]);

  const lojaGrupoOptions = useMemo(
    () => (lojaCompleta ? [lojaCompleta] : []),
    [lojaCompleta],
  );

  const handleRazaoSocialChange = useCallback(
    (v) => {
      setRazaoSocial(v);
      if (!v?.razaoSocial) {
        setLojaCompleta(null);
        return;
      }
      const matches = lojasCatalogo.filter(
        (l) => l.razaoSocial === v.razaoSocial,
      );
      if (matches.length === 0) {
        setLojaCompleta(null);
        return;
      }
      const sorted = [...matches].sort((a, b) => {
        const cmp = (a.grupoEconomico || "").localeCompare(
          b.grupoEconomico || "",
          "pt-BR",
        );
        if (cmp !== 0) return cmp;
        return String(a.id).localeCompare(String(b.id), "pt-BR");
      });
      setLojaCompleta(sorted[0]);
    },
    [lojasCatalogo],
  );

  const handleLojaCompletaChange = useCallback(
    (loja) => {
      setLojaCompleta(normalizeLojaForm(loja));
      if (!loja?.razaoSocial) return;
      const sample =
        lojasCatalogo.find((l) => l.id === loja.id) ??
        lojasCatalogo.find(
          (l) =>
            l.razaoSocial === loja.razaoSocial &&
            l.idGrupoEconomico === loja.idGrupoEconomico,
        );
      setRazaoSocial(
        buildRazaoSocialOption(loja.razaoSocial, sample?.cnpj ?? loja.cnpj),
      );
    },
    [lojasCatalogo],
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setColetas(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(coletas));
    } catch {
      if (!storageWarnedRef.current) {
        storageWarnedRef.current = true;
        snackbarRef.current.showMessage(
          "Não foi possível salvar o rascunho no armazenamento local (limite ou dados muito grandes).",
          { severity: "warning" },
        );
      }
    }
  }, [coletas]);

  const handleProdutoChange = useCallback(
    (v) => {
      setConcorrenteDestaqueId(null);
      if (!v) {
        produtoSelecionadoIdRef.current = null;
        setProduto(null);
        setConcorrentesRows([]);
        setPrecoKarstenLoja("");
        return;
      }
      const full =
        produtosCatalogo.find((c) => c.id != null && c.id === v.id) || v;
      const nextId = full?.id ?? null;
      if (produtoSelecionadoIdRef.current === nextId) {
        return;
      }
      produtoSelecionadoIdRef.current = nextId;
      setProduto(full);
      setPrecoKarstenLoja("");
      setConcorrentesRows(buildConcorrentesRowsFromProduto(full));
    },
    [produtosCatalogo],
  );

  const updateConcorrente = useCallback((index, patch) => {
    setConcorrentesRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.encontrado === false) {
          return {
            ...next,
            precoNormal: undefined,
            emPromocao: false,
            precoPromocional: undefined,
            fotoBase64: "",
          };
        }
        return next;
      }),
    );
  }, []);

  const isCurrentFormValid = useMemo(() => {
    if (!lojaCompleta || !produto) return false;
    const precoN = resolvePrecoKarstenNumerico(precoKarstenLoja);
    if (precoN == null || precoN <= 0) return false;
    for (const row of concorrentesRows) {
      if (!row.encontrado) continue;
      if (row.precoNormal == null || row.precoNormal <= 0) return false;
      if (!row.fotoBase64) return false;
      if (row.emPromocao) {
        if (row.precoPromocional == null || row.precoPromocional <= 0) {
          return false;
        }
      }
    }
    return true;
  }, [lojaCompleta, produto, precoKarstenLoja, concorrentesRows]);

  const buildPayloadItem = useCallback(() => {
    const precoSalvo = resolvePrecoKarstenNumerico(precoKarstenLoja);
    return {
      clientId: editingId ?? `sessao_${Date.now()}`,
      tipoColeta,
      dataColeta,
      representante,
      loja: lojaCompleta,
      produto,
      precoKarstenLoja: precoSalvo,
      concorrentes: concorrentesRows.map((r) => ({ ...r })),
    };
  }, [
    editingId,
    tipoColeta,
    dataColeta,
    representante,
    lojaCompleta,
    produto,
    precoKarstenLoja,
    concorrentesRows,
  ]);

  const handleAdicionarColeta = () => {
    if (!isCurrentFormValid) {
      snackbar.showMessage(
        "Preencha os campos obrigatórios: loja, SKU Karsten, preço Karsten e, para cada concorrente com produto encontrado, preço normal, foto e preço promocional quando em promoção.",
        { severity: "warning" },
      );
      return;
    }
    const item = buildPayloadItem();
    setColetas((prev) => {
      const idx = prev.findIndex((c) => c.clientId === item.clientId);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = item;
        return copy;
      }
      return [...prev, item];
    });
    setEditingId(null);
    produtoSelecionadoIdRef.current = null;
    setProduto(null);
    setPrecoKarstenLoja("");
    setConcorrentesRows([]);
    snackbar.showMessage("Produto adicionado à coleta da sessão.", {
      severity: "success",
    });
  };

  const handleEditColeta = (row) => {
    setEditingId(row.clientId ?? row.id);
    setTipoColeta(row.tipoColeta);
    setDataColeta(row.dataColeta);
    handleLojaCompletaChange(row.loja);
    const idRef = row.produto?.id;
    let refProduto = produtosCatalogo.find(
      (p) => p.id != null && p.id === idRef,
    );
    if (!refProduto && row.produto) {
      refProduto = {
        ...row.produto,
        listaSkuNome: `${row.produto.sku} - ${row.produto.nome}`,
        linha: row.produto.linha ?? "",
        categoria: row.produto.categoria ?? "",
        gramatura: row.produto.gramatura ?? "",
        tecidoBase: row.produto.tecidoBase ?? "",
        precoKarsten: row.precoKarstenLoja,
        imagemUrl: row.produto.imagemUrl ?? "",
        concorrentesPlanejados: [],
      };
    } else if (refProduto && row.produto) {
      refProduto = {
        ...refProduto,
        gramatura: row.produto.gramatura ?? refProduto.gramatura,
        tecidoBase: row.produto.tecidoBase ?? refProduto.tecidoBase,
      };
    }
    produtoSelecionadoIdRef.current = refProduto?.id ?? null;
    setProduto(refProduto || null);
    const precoRow = resolvePrecoKarstenNumerico(row.precoKarstenLoja);
    setPrecoKarstenLoja(precoRow != null ? precoRow : "");
    const catalogHint = produtosCatalogo.find(
      (p) => p.id != null && p.id === idRef,
    );
    const refPorId = {};
    (catalogHint?.concorrentesPlanejados ?? []).forEach((p) => {
      if (p.idProdutoConcorrente) refPorId[p.idProdutoConcorrente] = p;
    });
    setConcorrentesRows(
      [...(row.concorrentes || [])]
        .map((r) => {
          const inferredFound =
            r.precoNormal != null &&
            r.precoNormal !== "" &&
            !Number.isNaN(Number(r.precoNormal)) &&
            Number(r.precoNormal) > 0;
          const encontrado =
            typeof r.encontrado === "boolean" ? r.encontrado : inferredFound;
          const catalog = r.idProdutoConcorrente
            ? refPorId[r.idProdutoConcorrente]
            : null;
          return {
            idProdutoConcorrente:
              r.idProdutoConcorrente ?? catalog?.idProdutoConcorrente ?? null,
            nome: r.nome ?? catalog?.nome ?? "",
            nomeProduto:
              r.nomeProduto ??
              r.produtoReferencia ??
              catalog?.nomeProduto ??
              "",
            produtoReferencia:
              r.nomeProduto ??
              r.produtoReferencia ??
              catalog?.nomeProduto ??
              "",
            linha: r.linha ?? catalog?.linha ?? "",
            gramatura: r.gramatura ?? catalog?.gramatura ?? "",
            tecidoBase: r.tecidoBase ?? catalog?.tecidoBase ?? "",
            encontrado,
            precoNormal: r.precoNormal,
            emPromocao: r.emPromocao,
            precoPromocional: r.precoPromocional,
            fotoBase64: r.fotoBase64 || "",
          };
        })
        .sort(compareConcorrenteAlfabetico),
    );
  };

  const handleRemoveColeta = (clientId) => {
    setColetas((prev) => prev.filter((c) => (c.clientId ?? c.id) !== clientId));
    if (editingId === clientId) {
      setEditingId(null);
      produtoSelecionadoIdRef.current = null;
      setProduto(null);
      setPrecoKarstenLoja("");
      setConcorrentesRows([]);
    }
  };

  const handleFinalizar = async () => {
    if (!coletas.length) {
      snackbar.showMessage("Adicione ao menos um produto à coleta.", {
        severity: "warning",
      });
      return;
    }
    setFinalizando(true);
    try {
      const coletasPayload = coletas.map((item) => ({
        tipoColeta: item.tipoColeta,
        dataColeta: item.dataColeta,
        representante: item.representante,
        loja: mapLojaToApiPayload(item.loja),
        produto: mapProdutoToApiPayload(item.produto),
        precoKarstenLoja: item.precoKarstenLoja,
        concorrentes: (item.concorrentes || [])
          .map(mapConcorrenteRowToApi)
          .filter(Boolean),
      }));
      const res = await Api.post(
        "coletas",
        "priceMonitoring",
        { coletas: coletasPayload },
        true,
      );
      const result = unwrapApiResult(res);
      const msg =
        result?.mensagem ?? result?.Mensagem ?? "Coleta enviada com sucesso.";
      localStorage.removeItem(STORAGE_KEY);
      setColetas([]);
      setEditingId(null);
      snackbar.showMessage(msg, { severity: "success" });
    } catch (e) {
      snackbar.showMessage(
        e?.message ? `Erro ao enviar: ${e.message}` : "Erro ao enviar coleta.",
        { severity: "error" },
      );
    } finally {
      setFinalizando(false);
    }
  };

  const handleAbrirCadastroConcorrente = () => {
    if (!produto?.id) {
      snackbar.showMessage(
        "Selecione um produto Karsten antes de adicionar concorrentes.",
        { severity: "warning" },
      );
      return;
    }
    if (!produtoPermiteCadastroConcorrente(produto)) {
      snackbar.showMessage(
        "Este produto não pertence às categorias Cama ou Banho. Não é possível adicionar concorrentes.",
        { severity: "warning" },
      );
      return;
    }
    setCadastroConcorrenteOpen(true);
  };

  const podeAdicionarConcorrente = useMemo(
    () => produtoPermiteCadastroConcorrente(produto),
    [produto],
  );

  const handleConcorrenteCadastrado = (row, mensagem) => {
    setConcorrenteDestaqueId(row.idProdutoConcorrente ?? null);
    setConcorrentesRows((prev) => {
      const existsIdx = prev.findIndex(
        (r) =>
          row.idProdutoConcorrente != null &&
          r.idProdutoConcorrente === row.idProdutoConcorrente,
      );
      if (existsIdx >= 0) {
        const copy = [...prev];
        copy[existsIdx] = { ...copy[existsIdx], ...row, encontrado: true };
        return copy.sort(compareConcorrenteAlfabetico);
      }
      return [...prev, { ...row, encontrado: true }].sort(
        compareConcorrenteAlfabetico,
      );
    });

    setProdutosCatalogo((prev) =>
      prev.map((p) => {
        if (p.id !== produto.id) return p;
        const planejados = p.concorrentesPlanejados ?? [];
        const jaExiste = planejados.some(
          (pl) => pl.idProdutoConcorrente === row.idProdutoConcorrente,
        );
        if (jaExiste) return p;
        return {
          ...p,
          concorrentesPlanejados: [
            ...planejados,
            {
              idProdutoConcorrente: row.idProdutoConcorrente,
              nome: row.nome,
              nomeProduto: row.nomeProduto,
              linha: row.linha,
              gramatura: row.gramatura,
              tecidoBase: row.tecidoBase,
            },
          ].sort(compareConcorrenteAlfabetico),
        };
      }),
    );

    setProduto((current) => {
      if (!current || current.id !== produto.id) return current;
      const planejados = current.concorrentesPlanejados ?? [];
      const jaExiste = planejados.some(
        (pl) => pl.idProdutoConcorrente === row.idProdutoConcorrente,
      );
      if (jaExiste) return current;
      return {
        ...current,
        concorrentesPlanejados: [
          ...planejados,
          {
            idProdutoConcorrente: row.idProdutoConcorrente,
            nome: row.nome,
            nomeProduto: row.nomeProduto,
            linha: row.linha,
            gramatura: row.gramatura,
            tecidoBase: row.tecidoBase,
          },
        ].sort(compareConcorrenteAlfabetico),
      };
    });

    snackbar.showMessage(
      mensagem || "Produto concorrente adicionado à coleta.",
      { severity: "success" },
    );
  };

  const handleFotoChange = async (index, file) => {
    if (!file) {
      updateConcorrente(index, { fotoBase64: "" });
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      updateConcorrente(index, { fotoBase64: b64 });
    } catch {
      snackbar.showMessage("Não foi possível ler a imagem.", {
        severity: "error",
      });
    }
  };

  const formProps = {
    tipoColeta,
    setTipoColeta,
    dataColeta,
    setDataColeta,
    representante,
    razaoSocial,
    handleRazaoSocialChange,
    lojaCompleta,
    setLojaCompleta: handleLojaCompletaChange,
    razoesOptions,
    lojaGrupoOptions,
    produto,
    handleProdutoChange,
    produtosCatalogo,
    precoKarstenLoja,
    setPrecoKarstenLoja,
    concorrentesRows,
    concorrenteDestaqueId,
    updateConcorrente,
    handleFotoChange,
    coletas,
    handleEditColeta,
    handleRemoveColeta,
    handleAdicionarColeta,
    handleFinalizar,
    isCurrentFormValid,
    finalizando,
    onAbrirCadastroConcorrente: handleAbrirCadastroConcorrente,
    podeAdicionarConcorrente,
  };

  return (
    <ThemeProvider theme={mpTheme}>
      <div className={classes.pageRoot}>
        <div className={classes.inner}>
          {pageState === "loading" && (
            <MonitoramentoPrecoLoading classes={classes} />
          )}
          {pageState === "unavailable" && (
            <MonitoramentoPrecoUnavailable classes={classes} />
          )}
          {pageState === "ready" && (
            <>
              <MonitoramentoPrecoHeader
                classes={classes}
                onMinhasColetas={() => {
                  setMinhasColetasMountKey((k) => k + 1);
                  setMinhasColetasOpen(true);
                }}
              />
              <MonitoramentoPrecoMinhasColetasDialog
                key={minhasColetasMountKey}
                open={minhasColetasOpen}
                onClose={() => setMinhasColetasOpen(false)}
                classes={classes}
              />
              <MonitoramentoPrecoCadastroConcorrenteDialog
                open={cadastroConcorrenteOpen}
                onClose={() => setCadastroConcorrenteOpen(false)}
                produto={produto}
                marcasConcorrentes={marcasConcorrentes}
                opcoesCadastroPorCategoria={opcoesCadastroPorCategoria}
                classes={classes}
                onSaved={handleConcorrenteCadastrado}
              />
              <MonitoramentoPrecoFormSections
                classes={classes}
                form={formProps}
              />
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
